// ══════════════════════════════════════════════════════════════════════
// GQ Stats Library — institutional-grade performance metrics
// Pure functions. No DOM, no Supabase, no globals.
// Exposed as window.GQ.stats.* and also via ES module export.
// All return-array inputs are PERIOD returns (e.g. daily) as decimals
// (0.01 = 1%). Annualization assumes 252 trading days unless overridden.
// ══════════════════════════════════════════════════════════════════════

(function (root) {
  const ANN = 252;

  // ────────────────────────────────────────────────────────────────────
  // Basic moments
  // ────────────────────────────────────────────────────────────────────
  const sum  = a => a.reduce((s, x) => s + x, 0);
  const mean = a => a.length ? sum(a) / a.length : 0;
  const variance = (a, ddof = 1) => {
    if (a.length < 2) return 0;
    const m = mean(a);
    return a.reduce((s, x) => s + (x - m) ** 2, 0) / (a.length - ddof);
  };
  const stdev   = (a, ddof = 1) => Math.sqrt(variance(a, ddof));
  const skewness = a => {
    if (a.length < 3) return 0;
    const m = mean(a), s = stdev(a, 0);
    if (s === 0) return 0;
    return a.reduce((acc, x) => acc + ((x - m) / s) ** 3, 0) / a.length;
  };
  const kurtosis = a => {
    // excess kurtosis (Fisher)
    if (a.length < 4) return 0;
    const m = mean(a), s = stdev(a, 0);
    if (s === 0) return 0;
    return a.reduce((acc, x) => acc + ((x - m) / s) ** 4, 0) / a.length - 3;
  };
  const min = a => a.length ? Math.min.apply(null, a) : 0;
  const max = a => a.length ? Math.max.apply(null, a) : 0;
  const median = a => {
    if (!a.length) return 0;
    const s = [...a].sort((x, y) => x - y);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  };
  const quantile = (a, q) => {
    if (!a.length) return 0;
    const s = [...a].sort((x, y) => x - y);
    const idx = (s.length - 1) * q;
    const lo = Math.floor(idx), hi = Math.ceil(idx);
    return lo === hi ? s[lo] : s[lo] * (hi - idx) + s[hi] * (idx - lo);
  };

  // ────────────────────────────────────────────────────────────────────
  // Return calculations
  // ────────────────────────────────────────────────────────────────────
  // From equity series [v0, v1, ...] → simple returns [(v1-v0)/v0, ...]
  const equityToReturns = eq => {
    const r = [];
    for (let i = 1; i < eq.length; i++) r.push((eq[i] - eq[i - 1]) / eq[i - 1]);
    return r;
  };
  const equityToLogReturns = eq => {
    const r = [];
    for (let i = 1; i < eq.length; i++) r.push(Math.log(eq[i] / eq[i - 1]));
    return r;
  };
  const returnsToEquity = (rets, start = 1) => {
    const eq = [start];
    for (const r of rets) eq.push(eq[eq.length - 1] * (1 + r));
    return eq;
  };
  const totalReturn = rets => rets.reduce((acc, r) => acc * (1 + r), 1) - 1;
  const cagr = (rets, periodsPerYear = ANN) => {
    if (!rets.length) return 0;
    const tr = totalReturn(rets);
    const years = rets.length / periodsPerYear;
    return years > 0 ? (1 + tr) ** (1 / years) - 1 : 0;
  };
  const annualizedReturn = (rets, periodsPerYear = ANN) => mean(rets) * periodsPerYear;
  const annualizedVol    = (rets, periodsPerYear = ANN) => stdev(rets) * Math.sqrt(periodsPerYear);

  // ────────────────────────────────────────────────────────────────────
  // Risk-adjusted return metrics
  // ────────────────────────────────────────────────────────────────────
  const sharpe = (rets, rfPerPeriod = 0, periodsPerYear = ANN) => {
    if (rets.length < 2) return 0;
    const excess = rets.map(r => r - rfPerPeriod);
    const s = stdev(excess);
    return s > 0 ? (mean(excess) / s) * Math.sqrt(periodsPerYear) : 0;
  };
  const sortino = (rets, rfPerPeriod = 0, periodsPerYear = ANN, mar = 0) => {
    if (rets.length < 2) return 0;
    const excess = rets.map(r => r - rfPerPeriod);
    const downside = excess.filter(r => r < mar);
    if (!downside.length) return 0;
    const downsideStd = Math.sqrt(downside.reduce((s, x) => s + x * x, 0) / rets.length);
    return downsideStd > 0 ? (mean(excess) / downsideStd) * Math.sqrt(periodsPerYear) : 0;
  };
  const calmar = (rets, periodsPerYear = ANN) => {
    const cg = cagr(rets, periodsPerYear);
    const dd = maxDrawdown(returnsToEquity(rets));
    return dd.max > 0 ? cg / dd.max : 0;
  };
  const mar = calmar; // MAR = Calmar by Schwager's defn

  // Treynor = (Rp - Rf) / beta. Requires benchmark.
  const treynor = (rets, benchRets, rfPerPeriod = 0, periodsPerYear = ANN) => {
    const b = beta(rets, benchRets);
    if (b === 0) return 0;
    const excess = mean(rets.map((r, i) => r - rfPerPeriod));
    return (excess * periodsPerYear) / b;
  };
  // Jensen's alpha = Rp - [Rf + beta * (Rm - Rf)]
  const jensensAlpha = (rets, benchRets, rfPerPeriod = 0, periodsPerYear = ANN) => {
    const b = beta(rets, benchRets);
    const rp = mean(rets) * periodsPerYear;
    const rm = mean(benchRets) * periodsPerYear;
    const rf = rfPerPeriod * periodsPerYear;
    return rp - (rf + b * (rm - rf));
  };
  // Information Ratio = (Rp - Rb) / tracking error
  const informationRatio = (rets, benchRets, periodsPerYear = ANN) => {
    const n = Math.min(rets.length, benchRets.length);
    const active = [];
    for (let i = 0; i < n; i++) active.push(rets[i] - benchRets[i]);
    const te = stdev(active);
    return te > 0 ? (mean(active) / te) * Math.sqrt(periodsPerYear) : 0;
  };

  // Beta vs benchmark
  const beta = (rets, benchRets) => {
    const n = Math.min(rets.length, benchRets.length);
    if (n < 2) return 0;
    const r = rets.slice(0, n), b = benchRets.slice(0, n);
    const mr = mean(r), mb = mean(b);
    let cov = 0, varB = 0;
    for (let i = 0; i < n; i++) {
      cov += (r[i] - mr) * (b[i] - mb);
      varB += (b[i] - mb) ** 2;
    }
    return varB > 0 ? cov / varB : 0;
  };

  // Correlation (Pearson)
  const correlation = (a, b) => {
    const n = Math.min(a.length, b.length);
    if (n < 2) return 0;
    const ma = mean(a.slice(0, n)), mb = mean(b.slice(0, n));
    let num = 0, da = 0, db = 0;
    for (let i = 0; i < n; i++) {
      const x = a[i] - ma, y = b[i] - mb;
      num += x * y; da += x * x; db += y * y;
    }
    return (da > 0 && db > 0) ? num / Math.sqrt(da * db) : 0;
  };

  // ────────────────────────────────────────────────────────────────────
  // Drawdown analysis
  // ────────────────────────────────────────────────────────────────────
  const maxDrawdown = equity => {
    if (!equity.length) return { max: 0, duration: 0, peakIdx: 0, troughIdx: 0, recoveryIdx: -1 };
    let peak = equity[0], peakIdx = 0, troughIdx = 0;
    let maxDD = 0, ddStartIdx = 0, ddDuration = 0, recoveryIdx = -1;
    let currentPeak = equity[0], currentPeakIdx = 0;
    for (let i = 1; i < equity.length; i++) {
      if (equity[i] > currentPeak) {
        if (maxDD > 0 && recoveryIdx === -1 && equity[i] >= peak) recoveryIdx = i;
        currentPeak = equity[i]; currentPeakIdx = i;
      }
      const dd = (currentPeak - equity[i]) / currentPeak;
      if (dd > maxDD) {
        maxDD = dd; peak = currentPeak; peakIdx = currentPeakIdx;
        troughIdx = i; ddStartIdx = currentPeakIdx;
        ddDuration = i - currentPeakIdx; recoveryIdx = -1;
      }
    }
    return { max: maxDD, duration: ddDuration, peakIdx, troughIdx, recoveryIdx };
  };

  // Underwater series: [(equity / running peak) - 1]
  const drawdownSeries = equity => {
    const out = []; let peak = -Infinity;
    for (const v of equity) {
      if (v > peak) peak = v;
      out.push(peak > 0 ? v / peak - 1 : 0);
    }
    return out;
  };

  // List of all drawdown episodes [{peakIdx, troughIdx, recoveryIdx, depth, duration}]
  const drawdownEpisodes = equity => {
    const episodes = [];
    if (equity.length < 2) return episodes;
    let peak = equity[0], peakIdx = 0, inDD = false;
    let trough = peak, troughIdx = 0;
    for (let i = 1; i < equity.length; i++) {
      if (equity[i] >= peak) {
        if (inDD) {
          episodes.push({
            peakIdx, troughIdx, recoveryIdx: i,
            depth: (peak - trough) / peak,
            duration: i - peakIdx,
            recoveryDuration: i - troughIdx,
          });
          inDD = false;
        }
        peak = equity[i]; peakIdx = i; trough = peak; troughIdx = i;
      } else {
        inDD = true;
        if (equity[i] < trough) { trough = equity[i]; troughIdx = i; }
      }
    }
    if (inDD) {
      episodes.push({
        peakIdx, troughIdx, recoveryIdx: -1,
        depth: (peak - trough) / peak,
        duration: equity.length - 1 - peakIdx,
        recoveryDuration: -1,
      });
    }
    return episodes;
  };

  // Ulcer Index = sqrt(mean(drawdown^2))
  const ulcerIndex = equity => {
    const dds = drawdownSeries(equity);
    return Math.sqrt(dds.reduce((s, d) => s + d * d, 0) / Math.max(1, dds.length));
  };
  // Pain Index = mean(|drawdown|)
  const painIndex = equity => {
    const dds = drawdownSeries(equity);
    return dds.reduce((s, d) => s + Math.abs(d), 0) / Math.max(1, dds.length);
  };
  // Conditional Drawdown at Risk = mean of worst alpha% drawdowns
  const cdar = (equity, alpha = 0.05) => {
    const dds = drawdownSeries(equity).map(d => -d).sort((a, b) => b - a);
    const k = Math.max(1, Math.floor(dds.length * alpha));
    return dds.slice(0, k).reduce((s, x) => s + x, 0) / k;
  };

  // ────────────────────────────────────────────────────────────────────
  // Tail risk — VaR, CVaR, Expected Shortfall
  // ────────────────────────────────────────────────────────────────────
  // Historical VaR — returns positive number = loss
  const varHistorical = (rets, alpha = 0.05) => {
    if (!rets.length) return 0;
    return -quantile(rets, alpha);
  };
  // Historical CVaR / Expected Shortfall = mean of worst tail
  const cvarHistorical = (rets, alpha = 0.05) => {
    if (!rets.length) return 0;
    const sorted = [...rets].sort((a, b) => a - b);
    const k = Math.max(1, Math.floor(sorted.length * alpha));
    const tail = sorted.slice(0, k);
    return -mean(tail);
  };
  // Parametric (normal) VaR
  const varParametricNormal = (rets, alpha = 0.05) => {
    const m = mean(rets), s = stdev(rets);
    return -(m + s * normInverse(alpha));
  };
  // Parametric (Student-t) VaR using fitted df
  const varParametricStudentT = (rets, alpha = 0.05) => {
    const m = mean(rets), s = stdev(rets);
    const df = fitStudentTDf(rets);
    return -(m + s * studentTInverse(alpha, df));
  };
  // EVT / Peaks-Over-Threshold VaR using GPD fit on excesses above 90th-pct
  const varEVT = (rets, alpha = 0.01, threshPctile = 0.10) => {
    if (!rets.length) return 0;
    const losses = rets.map(r => -r);
    const threshold = quantile(losses, 1 - threshPctile);
    const excesses = losses.filter(l => l > threshold).map(l => l - threshold);
    if (excesses.length < 5) return varHistorical(rets, alpha);
    const { xi, beta } = gpdFitMLE(excesses);
    const Nu = excesses.length, N = losses.length;
    const q = threshold + (beta / xi) * (((N / Nu) * alpha) ** (-xi) - 1);
    return q;
  };

  // ────────────────────────────────────────────────────────────────────
  // Trade statistics — input: array of {pnl, side, entry, exit, ...}
  // ────────────────────────────────────────────────────────────────────
  const winRate = trades => {
    if (!trades.length) return 0;
    return trades.filter(t => t.pnl > 0).length / trades.length;
  };
  const profitFactor = trades => {
    const gross_w = trades.filter(t => t.pnl > 0).reduce((s, t) => s + t.pnl, 0);
    const gross_l = -trades.filter(t => t.pnl < 0).reduce((s, t) => s + t.pnl, 0);
    return gross_l > 0 ? gross_w / gross_l : (gross_w > 0 ? Infinity : 0);
  };
  const expectancy = trades => {
    if (!trades.length) return 0;
    return mean(trades.map(t => t.pnl));
  };
  const payoffRatio = trades => {
    const wins = trades.filter(t => t.pnl > 0).map(t => t.pnl);
    const losses = trades.filter(t => t.pnl < 0).map(t => Math.abs(t.pnl));
    return (wins.length && losses.length) ? mean(wins) / mean(losses) : 0;
  };
  // Kelly fraction: f* = W - (1-W)/R where W = win prob, R = avg_win / avg_loss
  const kellyFraction = trades => {
    const w = winRate(trades), pr = payoffRatio(trades);
    if (pr === 0) return 0;
    return Math.max(0, w - (1 - w) / pr);
  };
  // Consecutive runs
  const consecutiveStreaks = trades => {
    let curW = 0, curL = 0, maxW = 0, maxL = 0;
    for (const t of trades) {
      if (t.pnl > 0)  { curW++; curL = 0; if (curW > maxW) maxW = curW; }
      else if (t.pnl < 0) { curL++; curW = 0; if (curL > maxL) maxL = curL; }
    }
    return { maxWinStreak: maxW, maxLossStreak: maxL };
  };
  // R-multiples = pnl / initial risk per trade
  const rMultiples = trades => trades
    .filter(t => t.risk && t.risk > 0)
    .map(t => t.pnl / t.risk);
  // MAE / MFE — Maximum Adverse / Favorable Excursion
  const maeSummary = trades => {
    const mae = trades.filter(t => t.mae !== undefined).map(t => t.mae);
    return mae.length ? { mean: mean(mae), max: max(mae) } : null;
  };
  const mfeSummary = trades => {
    const mfe = trades.filter(t => t.mfe !== undefined).map(t => t.mfe);
    return mfe.length ? { mean: mean(mfe), max: max(mfe) } : null;
  };

  // ────────────────────────────────────────────────────────────────────
  // Robust / advanced Sharpe metrics (Bailey, López de Prado)
  // ────────────────────────────────────────────────────────────────────
  // Probabilistic Sharpe Ratio
  const psr = (rets, srBenchmark = 0, periodsPerYear = ANN) => {
    if (rets.length < 4) return 0;
    const sr = sharpe(rets, 0, periodsPerYear);
    const srPeriod = sr / Math.sqrt(periodsPerYear);
    const skew = skewness(rets);
    const kurt = kurtosis(rets);
    const T = rets.length;
    const num = (srPeriod - srBenchmark / Math.sqrt(periodsPerYear)) * Math.sqrt(T - 1);
    const den = Math.sqrt(1 - skew * srPeriod + ((kurt) / 4) * srPeriod * srPeriod);
    return den > 0 ? normCDF(num / den) : 0;
  };
  // Deflated Sharpe Ratio — adjusts for multiple-trial selection bias
  // sharpes = SR (annualised) of every candidate strategy tried
  const dsr = (rets, sharpes, periodsPerYear = ANN) => {
    if (!sharpes || sharpes.length < 2) return psr(rets, 0, periodsPerYear);
    const meanSR = mean(sharpes), varSR = variance(sharpes);
    const N = sharpes.length;
    const eulerMascheroni = 0.5772156649;
    const expectedMaxSR = Math.sqrt(varSR) * (
      (1 - eulerMascheroni) * normInverse(1 - 1 / N) +
      eulerMascheroni * normInverse(1 - 1 / (N * Math.E))
    ) + meanSR;
    return psr(rets, expectedMaxSR, periodsPerYear);
  };
  // Bootstrap Sharpe CI (Lo 2002): non-parametric
  const sharpeBootstrapCI = (rets, alpha = 0.05, nBoot = 1000, periodsPerYear = ANN) => {
    const N = rets.length;
    const samples = [];
    for (let b = 0; b < nBoot; b++) {
      const sample = [];
      for (let i = 0; i < N; i++) sample.push(rets[Math.floor(Math.random() * N)]);
      samples.push(sharpe(sample, 0, periodsPerYear));
    }
    return { lo: quantile(samples, alpha / 2), hi: quantile(samples, 1 - alpha / 2), mean: mean(samples) };
  };

  // ────────────────────────────────────────────────────────────────────
  // Rolling versions
  // ────────────────────────────────────────────────────────────────────
  const rolling = (arr, window, fn) => {
    const out = new Array(arr.length).fill(null);
    for (let i = window - 1; i < arr.length; i++) out[i] = fn(arr.slice(i - window + 1, i + 1));
    return out;
  };
  const rollingSharpe   = (rets, window, periodsPerYear = ANN) => rolling(rets, window, s => sharpe(s, 0, periodsPerYear));
  const rollingSortino  = (rets, window, periodsPerYear = ANN) => rolling(rets, window, s => sortino(s, 0, periodsPerYear));
  const rollingVol      = (rets, window, periodsPerYear = ANN) => rolling(rets, window, s => annualizedVol(s, periodsPerYear));
  const rollingMean     = (arr, window) => rolling(arr, window, mean);
  const rollingBeta     = (rets, bench, window) => {
    const out = new Array(rets.length).fill(null);
    for (let i = window - 1; i < rets.length; i++) {
      out[i] = beta(rets.slice(i - window + 1, i + 1), bench.slice(i - window + 1, i + 1));
    }
    return out;
  };
  const rollingCorrelation = (a, b, window) => {
    const out = new Array(a.length).fill(null);
    for (let i = window - 1; i < a.length; i++) {
      out[i] = correlation(a.slice(i - window + 1, i + 1), b.slice(i - window + 1, i + 1));
    }
    return out;
  };

  // ────────────────────────────────────────────────────────────────────
  // Distribution helpers (used by parametric VaR, PSR, etc.)
  // ────────────────────────────────────────────────────────────────────
  // Standard normal CDF — Abramowitz & Stegun 26.2.17
  function normCDF(x) {
    const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
    const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x) / Math.sqrt(2);
    const t = 1 / (1 + p * x);
    const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    return 0.5 * (1 + sign * y);
  }
  // Standard normal inverse CDF (Beasley-Springer-Moro)
  function normInverse(p) {
    if (p <= 0 || p >= 1) return p <= 0 ? -Infinity : Infinity;
    const a = [-3.969683028665376e+01,  2.209460984245205e+02, -2.759285104469687e+02,
                1.383577518672690e+02, -3.066479806614716e+01,  2.506628277459239e+00];
    const b = [-5.447609879822406e+01,  1.615858368580409e+02, -1.556989798598866e+02,
                6.680131188771972e+01, -1.328068155288572e+01];
    const c = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00,
               -2.549732539343734e+00,  4.374664141464968e+00,  2.938163982698783e+00];
    const d = [7.784695709041462e-03, 3.224671290700398e-01,
               2.445134137142996e+00, 3.754408661907416e+00];
    const plow = 0.02425, phigh = 1 - plow;
    let q, r;
    if (p < plow) {
      q = Math.sqrt(-2 * Math.log(p));
      return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
             ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
    } else if (p <= phigh) {
      q = p - 0.5; r = q * q;
      return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q /
             (((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1);
    } else {
      q = Math.sqrt(-2 * Math.log(1 - p));
      return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
              ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
    }
  }
  // Student-t inverse — rough Cornish-Fisher approximation; good enough for risk metrics
  function studentTInverse(p, df) {
    const z = normInverse(p);
    if (df > 30) return z;
    // Cornish-Fisher expansion
    const g1 = (z ** 3 + z) / (4 * df);
    const g2 = (5 * z ** 5 + 16 * z ** 3 + 3 * z) / (96 * df * df);
    return z + g1 + g2;
  }
  // Fit Student-t degrees of freedom via method of moments using sample kurtosis.
  // Excess kurtosis k = 6 / (df - 4)  ⇒ df = 6/k + 4
  function fitStudentTDf(rets) {
    const k = kurtosis(rets);
    if (k <= 0) return 100; // ~normal
    const df = 6 / k + 4;
    return Math.max(2.5, Math.min(100, df));
  }
  // Generalised Pareto Distribution — MLE for shape (xi) and scale (beta)
  // Newton-Raphson on profile log-likelihood (Grimshaw 1993, simplified)
  function gpdFitMLE(excesses) {
    if (excesses.length < 5) return { xi: 0.1, beta: mean(excesses) };
    // Start from method-of-moments
    const m = mean(excesses), s2 = variance(excesses, 0);
    let xi = 0.5 * (1 - (m * m) / Math.max(1e-12, s2));
    let beta = 0.5 * m * (1 + (m * m) / Math.max(1e-12, s2));
    // 30 Newton steps on profile likelihood
    for (let iter = 0; iter < 30; iter++) {
      let dxi = 0, dxx = 0, n = excesses.length;
      for (const y of excesses) {
        const z = 1 + xi * y / beta;
        if (z <= 0) break;
        dxi += (Math.log(z) / (xi * xi)) - ((1 + xi) * y) / (beta * xi * z);
        dxx += -(2 * Math.log(z) / (xi ** 3)) + (y / (beta * xi * xi * z)) * 2 + ((1 + xi) * y * y) / ((beta * z) ** 2 * xi);
      }
      const step = dxi / (Math.abs(dxx) + 1e-9);
      xi -= step;
      if (xi < -0.45) xi = -0.45;
      if (xi >  1.5)  xi =  1.5;
      if (Math.abs(step) < 1e-7) break;
    }
    // Closed-form beta given xi
    const n = excesses.length, sm = sum(excesses);
    beta = (sm + n * xi * mean(excesses)) / n;
    if (beta <= 0) beta = Math.max(1e-6, mean(excesses));
    return { xi, beta };
  }

  // ────────────────────────────────────────────────────────────────────
  // PCA — eigen-decomposition of covariance matrix (Jacobi rotations)
  // Input: NxK matrix of returns; returns eigenvalues + eigenvectors
  // ────────────────────────────────────────────────────────────────────
  function covarianceMatrix(returnsMatrix) {
    const N = returnsMatrix.length, K = returnsMatrix[0].length;
    const means = new Array(K).fill(0);
    for (let k = 0; k < K; k++) means[k] = mean(returnsMatrix.map(r => r[k]));
    const cov = Array.from({ length: K }, () => new Array(K).fill(0));
    for (let i = 0; i < K; i++) for (let j = i; j < K; j++) {
      let s = 0;
      for (let n = 0; n < N; n++) s += (returnsMatrix[n][i] - means[i]) * (returnsMatrix[n][j] - means[j]);
      cov[i][j] = cov[j][i] = s / Math.max(1, N - 1);
    }
    return cov;
  }
  // Jacobi eigen-decomposition for symmetric matrices
  function eigSymmetric(A) {
    const n = A.length;
    const V = Array.from({ length: n }, (_, i) =>
      Array.from({ length: n }, (_, j) => (i === j ? 1 : 0)));
    const M = A.map(row => row.slice());
    const maxIter = 100, tol = 1e-10;
    for (let iter = 0; iter < maxIter; iter++) {
      let p = 0, q = 1, mx = 0;
      for (let i = 0; i < n - 1; i++) for (let j = i + 1; j < n; j++) {
        if (Math.abs(M[i][j]) > mx) { mx = Math.abs(M[i][j]); p = i; q = j; }
      }
      if (mx < tol) break;
      const theta = (M[q][q] - M[p][p]) / (2 * M[p][q]);
      const t = Math.sign(theta) / (Math.abs(theta) + Math.sqrt(1 + theta * theta));
      const c = 1 / Math.sqrt(1 + t * t), s = t * c;
      // Rotate
      const Mpp = M[p][p], Mqq = M[q][q], Mpq = M[p][q];
      M[p][p] = Mpp - t * Mpq;
      M[q][q] = Mqq + t * Mpq;
      M[p][q] = M[q][p] = 0;
      for (let i = 0; i < n; i++) if (i !== p && i !== q) {
        const Mip = M[i][p], Miq = M[i][q];
        M[i][p] = M[p][i] = c * Mip - s * Miq;
        M[i][q] = M[q][i] = s * Mip + c * Miq;
      }
      for (let i = 0; i < n; i++) {
        const Vip = V[i][p], Viq = V[i][q];
        V[i][p] = c * Vip - s * Viq;
        V[i][q] = s * Vip + c * Viq;
      }
    }
    const eigenvalues = M.map((row, i) => row[i]);
    // Sort descending by eigenvalue
    const idx = eigenvalues.map((v, i) => i).sort((a, b) => eigenvalues[b] - eigenvalues[a]);
    return {
      eigenvalues: idx.map(i => eigenvalues[i]),
      eigenvectors: idx.map(i => V.map(row => row[i])), // each column = an eigenvector
    };
  }
  function pca(returnsMatrix) {
    const cov = covarianceMatrix(returnsMatrix);
    const { eigenvalues, eigenvectors } = eigSymmetric(cov);
    const totalVar = sum(eigenvalues);
    return {
      eigenvalues,
      eigenvectors,
      explained: eigenvalues.map(v => v / totalVar),
      explainedCumulative: eigenvalues.reduce((acc, v, i) => {
        acc.push((acc[i - 1] || 0) + v / totalVar);
        return acc;
      }, []),
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // OLS multivariate regression — for Fama-French style factor exposures
  // Solves y = X b + e for b via normal equations  (X'X)b = X'y
  // ────────────────────────────────────────────────────────────────────
  function olsRegression(y, X, includeIntercept = true) {
    // Add intercept column if requested
    const Xm = includeIntercept ? X.map(row => [1, ...row]) : X.map(row => row.slice());
    const n = Xm.length, k = Xm[0].length;
    // X'X
    const XtX = Array.from({ length: k }, () => new Array(k).fill(0));
    for (let i = 0; i < k; i++) for (let j = 0; j < k; j++) {
      let s = 0; for (let r = 0; r < n; r++) s += Xm[r][i] * Xm[r][j];
      XtX[i][j] = s;
    }
    // X'y
    const Xty = new Array(k).fill(0);
    for (let i = 0; i < k; i++) {
      let s = 0; for (let r = 0; r < n; r++) s += Xm[r][i] * y[r];
      Xty[i] = s;
    }
    // Solve via Gauss-Jordan
    const A = XtX.map((row, i) => [...row, Xty[i]]);
    for (let i = 0; i < k; i++) {
      // Find pivot
      let maxRow = i;
      for (let r = i + 1; r < k; r++) if (Math.abs(A[r][i]) > Math.abs(A[maxRow][i])) maxRow = r;
      [A[i], A[maxRow]] = [A[maxRow], A[i]];
      const piv = A[i][i];
      if (Math.abs(piv) < 1e-12) return { coef: new Array(k).fill(0), r2: 0 };
      for (let j = i; j <= k; j++) A[i][j] /= piv;
      for (let r = 0; r < k; r++) if (r !== i) {
        const f = A[r][i];
        for (let j = i; j <= k; j++) A[r][j] -= f * A[i][j];
      }
    }
    const coef = A.map(row => row[k]);
    // R²
    const yhat = Xm.map(row => row.reduce((s, v, i) => s + v * coef[i], 0));
    const ssRes = y.reduce((s, v, i) => s + (v - yhat[i]) ** 2, 0);
    const ymean = mean(y);
    const ssTot = y.reduce((s, v) => s + (v - ymean) ** 2, 0);
    return { coef, r2: ssTot > 0 ? 1 - ssRes / ssTot : 0, residuals: y.map((v, i) => v - yhat[i]) };
  }

  // ────────────────────────────────────────────────────────────────────
  // KPI bundle — call this once after a backtest, get everything
  // ────────────────────────────────────────────────────────────────────
  function fullKPIs({ equity, trades = [], benchEquity = null, rfPerPeriod = 0, periodsPerYear = ANN }) {
    const rets = equityToReturns(equity);
    const benchRets = benchEquity ? equityToReturns(benchEquity) : null;
    const dd = maxDrawdown(equity);
    const out = {
      totalReturn:       totalReturn(rets),
      cagr:              cagr(rets, periodsPerYear),
      annualizedReturn:  annualizedReturn(rets, periodsPerYear),
      annualizedVol:     annualizedVol(rets, periodsPerYear),
      sharpe:            sharpe(rets, rfPerPeriod, periodsPerYear),
      sortino:           sortino(rets, rfPerPeriod, periodsPerYear),
      calmar:            calmar(rets, periodsPerYear),
      maxDrawdown:       dd.max,
      maxDDDuration:     dd.duration,
      ulcerIndex:        ulcerIndex(equity),
      painIndex:         painIndex(equity),
      cdar95:            cdar(equity, 0.05),
      var95:             varHistorical(rets, 0.05),
      var99:             varHistorical(rets, 0.01),
      cvar95:            cvarHistorical(rets, 0.05),
      cvar99:            cvarHistorical(rets, 0.01),
      varParam95:        varParametricNormal(rets, 0.05),
      varStudentT95:     varParametricStudentT(rets, 0.05),
      varEVT99:          varEVT(rets, 0.01),
      skewness:          skewness(rets),
      kurtosis:          kurtosis(rets),
      winRate:           winRate(trades),
      profitFactor:      profitFactor(trades),
      expectancy:        expectancy(trades),
      payoffRatio:       payoffRatio(trades),
      kellyFraction:     kellyFraction(trades),
      streaks:           consecutiveStreaks(trades),
      tradeCount:        trades.length,
      psr:               psr(rets, 0, periodsPerYear),
    };
    if (benchRets) {
      out.beta              = beta(rets, benchRets);
      out.alpha             = jensensAlpha(rets, benchRets, rfPerPeriod, periodsPerYear);
      out.informationRatio  = informationRatio(rets, benchRets, periodsPerYear);
      out.treynor           = treynor(rets, benchRets, rfPerPeriod, periodsPerYear);
      out.correlation       = correlation(rets, benchRets);
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // Public surface
  // ────────────────────────────────────────────────────────────────────
  const api = {
    // moments
    sum, mean, variance, stdev, skewness, kurtosis,
    min, max, median, quantile,
    // returns
    equityToReturns, equityToLogReturns, returnsToEquity,
    totalReturn, cagr, annualizedReturn, annualizedVol,
    // risk-adjusted
    sharpe, sortino, calmar, mar, treynor, jensensAlpha,
    informationRatio, beta, correlation,
    // drawdown
    maxDrawdown, drawdownSeries, drawdownEpisodes,
    ulcerIndex, painIndex, cdar,
    // tail
    varHistorical, cvarHistorical, varParametricNormal,
    varParametricStudentT, varEVT,
    // trade stats
    winRate, profitFactor, expectancy, payoffRatio, kellyFraction,
    consecutiveStreaks, rMultiples, maeSummary, mfeSummary,
    // robust SR
    psr, dsr, sharpeBootstrapCI,
    // rolling
    rolling, rollingSharpe, rollingSortino, rollingVol,
    rollingMean, rollingBeta, rollingCorrelation,
    // distribution helpers
    normCDF, normInverse, studentTInverse, fitStudentTDf, gpdFitMLE,
    // matrix
    covarianceMatrix, eigSymmetric, pca, olsRegression,
    // bundle
    fullKPIs,
    // constants
    ANN,
  };

  // Expose globally
  if (root) {
    root.GQ = root.GQ || {};
    root.GQ.stats = api;
  }
  // Module export (if supported)
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
